import { useCallback, useEffect, useRef, useState } from 'react';

import { formatBitrate, formatResolution } from './format.js';

const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,

  // Assume a decent link until measurement says otherwise. This is the single biggest
  // lever on time-to-high-quality: the default estimate is conservative and costs
  // several segments of climbing before the top rendition is reached.
  abrEwmaDefaultEstimate: 5_000_000,
  startLevel: -1,

  // Never fetch a rendition finer than the display can resolve (device pixel ratio is
  // taken into account). Set to false to always pull the top rendition regardless of
  // element size — costs bandwidth for no visible gain on phones.
  capLevelToPlayerSize: true,

  // Generous buffers: this is VOD to a handful of viewers, so trading memory for
  // stall-resistance is free.
  maxBufferLength: 30,
  maxMaxBufferLength: 600,
  backBufferLength: 90,

  fragLoadingMaxRetry: 4,
  manifestLoadingMaxRetry: 3,
};

/**
 * iOS has no reliable MSE path, so Safari on iPhone/iPad must use the native player.
 * Everywhere else hls.js wins: a real quality selector, measurable ABR, and identical
 * behaviour across Chrome, Firefox and Edge.
 */
function isAppleMobile() {
  const ua = navigator.userAgent;
  const iPadOsDesktopMode = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOsDesktopMode;
}

/**
 * Chooses the engine *without* loading hls.js, so that the library page and every iOS
 * viewer never download it. MSE presence is the same signal Hls.isSupported() checks.
 */
function pickEngine(element) {
  const native = element.canPlayType('application/vnd.apple.mpegurl') !== '';
  const hasMse =
    typeof window.MediaSource !== 'undefined' || typeof window.ManagedMediaSource !== 'undefined';

  if (isAppleMobile() && native) return 'native';
  if (hasMse) return 'hlsjs';
  if (native) return 'native';
  return 'unsupported';
}

export default function Player({ video, autoPlay = false }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [engine, setEngine] = useState(null);
  const [levels, setLevels] = useState([]);
  const [pinnedLevel, setPinnedLevel] = useState(-1);
  const [activeLevel, setActiveLevel] = useState(-1);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [canFullscreen, setCanFullscreen] = useState(false);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return undefined;

    setLevels([]);
    setPinnedLevel(-1);
    setActiveLevel(-1);
    setError(null);

    const chosen = pickEngine(element);
    setEngine(chosen);

    if (chosen === 'unsupported') {
      setError('This browser cannot play HLS.');
      return undefined;
    }

    const playNative = () => {
      setEngine('native');
      element.src = video.src;
    };

    const teardown = () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
      // Covers the native path and the hls.js-to-native fallback alike.
      element.removeAttribute('src');
      element.load();
    };

    if (chosen === 'native') {
      playNative();
      return teardown;
    }

    let disposed = false;

    // Loaded on demand: hls.js is by far the largest dependency, and it is dead weight
    // on the library page and on every iOS device. The full build, not hls.js/light:
    // the encoder ships audio as a separate rendition (iOS rejects muxed audio), and
    // the light build has no audio-track controller, so it would play silently.
    import('hls.js')
      .then(({ default: Hls }) => {
        if (disposed) return;

        if (!Hls.isSupported()) {
          if (element.canPlayType('application/vnd.apple.mpegurl') !== '') playNative();
          else setError('This browser cannot play HLS.');
          return;
        }

        const hls = new Hls(HLS_CONFIG);
        hlsRef.current = hls;

        hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          setLevels(
            data.levels.map((level, index) => ({
              index,
              height: level.height ?? null,
              bitrate: level.bitrate ?? null,
              codec: level.videoCodec ?? null,
            })),
          );
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => setActiveLevel(data.level));

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;

          // Media errors are usually recoverable in place; a fatal network error on a
          // manifest is not, so surface that to the viewer.
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          setError(`Playback failed: ${data.details}`);
          hls.destroy();
          hlsRef.current = null;
        });

        hls.loadSource(video.src);
        hls.attachMedia(element);
      })
      .catch(() => {
        if (!disposed) setError('Could not load the playback engine.');
      });

    return () => {
      disposed = true;
      teardown();
    };
  }, [video.src]);

  // iPhone never exposes Element.requestFullscreen, and the video element only
  // admits to supporting fullscreen once it has metadata, so this has to be
  // re-checked rather than read once at mount.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return undefined;

    const check = () =>
      setCanFullscreen(
        element.webkitSupportsFullscreen === true ||
          (typeof element.requestFullscreen === 'function' && document.fullscreenEnabled),
      );

    check();
    element.addEventListener('loadedmetadata', check);
    return () => element.removeEventListener('loadedmetadata', check);
  }, [video.src]);

  // Live read-out of what the player is actually doing — the fastest way to confirm
  // the delivery path is behaving on a real device.
  useEffect(() => {
    if (engine !== 'hlsjs') return undefined;

    const timer = setInterval(() => {
      const hls = hlsRef.current;
      const element = videoRef.current;
      if (!hls || !element) return;

      const buffered = element.buffered;
      const ahead =
        buffered.length > 0
          ? Math.max(0, buffered.end(buffered.length - 1) - element.currentTime)
          : 0;

      setStats({
        estimate: hls.bandwidthEstimate,
        buffered: ahead,
        dropped: element.getVideoPlaybackQuality?.().droppedVideoFrames ?? 0,
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [engine]);

  /**
   * On iPhone the only way into fullscreen is the video element's own WebKit
   * method, which hands the stream to the native player — the one that follows
   * the phone's rotation. Everywhere else the whole player goes fullscreen, so
   * the quality buttons stay reachable.
   */
  const enterFullscreen = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    if (typeof element.webkitEnterFullscreen === 'function') {
      element.webkitEnterFullscreen();
      return;
    }
    (containerRef.current ?? element).requestFullscreen?.();
  }, []);

  const selectLevel = useCallback((index) => {
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = index;
    setPinnedLevel(index);
  }, []);

  const current = levels.find((level) => level.index === activeLevel) ?? null;

  return (
    <div className="player" ref={containerRef}>
      <video
        ref={videoRef}
        className="player__video"
        poster={video.poster ?? undefined}
        controls
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
      />

      {error && <p className="player__error">{error}</p>}

      <div className="player__bar">
        {engine === 'hlsjs' && levels.length > 0 && (
          <div className="quality" role="group" aria-label="Quality">
            <button
              type="button"
              className={`quality__btn${pinnedLevel === -1 ? ' quality__btn--on' : ''}`}
              onClick={() => selectLevel(-1)}
              aria-pressed={pinnedLevel === -1}
            >
              Auto
              {pinnedLevel === -1 && current?.height ? ` · ${formatResolution(current.height)}` : ''}
            </button>

            {levels
              .slice()
              .sort((a, b) => (b.height ?? 0) - (a.height ?? 0))
              .map((level) => (
                <button
                  key={level.index}
                  type="button"
                  className={`quality__btn${pinnedLevel === level.index ? ' quality__btn--on' : ''}`}
                  onClick={() => selectLevel(level.index)}
                  aria-pressed={pinnedLevel === level.index}
                  title={[formatBitrate(level.bitrate), level.codec].filter(Boolean).join(' · ')}
                >
                  {formatResolution(level.height) ?? `#${level.index}`}
                </button>
              ))}
          </div>
        )}

        {engine === 'native' && <span className="player__note">Native HLS · quality is automatic</span>}

        {canFullscreen && (
          <button type="button" className="player__fs" onClick={enterFullscreen}>
            Fullscreen
          </button>
        )}

        {stats && (
          <dl className="stats">
            <div>
              <dt>Link</dt>
              <dd>{formatBitrate(stats.estimate) ?? '—'}</dd>
            </div>
            <div>
              <dt>Buffer</dt>
              <dd>{stats.buffered.toFixed(1)}s</dd>
            </div>
            <div>
              <dt>Dropped</dt>
              <dd>{stats.dropped}</dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}
